/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, {
  ElementType,
  KeyboardEvent,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useLatest from '../../hooks/use-latest';
import { Collapse } from '../../transitions/collapse/collapse';
import { TAB_KEY } from '../../utils/keyboard';
import { AccordionItemButton } from './accordion-item-button';
import styles from './accordion-item.module.sass';

export interface AccordionItemConfig {
  itemKey: string;
  onToggleItem?(key: string, open: boolean): void;
  /**
   * KU
   *
   * effectively the "initial" open state in uncontrolled mode;
   * should be specified in controlled mode
   */
  isOpen?: boolean;
  /**
   *
   * KU
   *
   * specify this if you want the {@link AccordionItem} to be a controlled component
   */
  setIsOpen?(open: boolean): void;
}

export function useAccordionItem({
  isOpen,
  onToggleItem,
  setIsOpen,
  itemKey,
}: AccordionItemConfig) {
  const [localOpenState, setLocalOpenState] = useState(!!isOpen);
  useEffect(() => setLocalOpenState(!!isOpen), [isOpen]);

  const latest = useLatest(localOpenState);

  // KU – allow accordion-item to be controlled or uncontrolled
  const toggle = setIsOpen || setLocalOpenState;

  const handleToggle = useCallback(
    (opened: boolean) => {
      onToggleItem?.(itemKey, opened);
      toggle(opened);
    },
    [onToggleItem, setIsOpen],
  );

  const onClick = useCallback(() => handleToggle?.(!latest.current), [handleToggle]);

  /** KU
   *
   * prefer `keyup` because focus handle will get triggered again when the
   * browser window regains focus. */
  const onKeyUp = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === TAB_KEY) {
        handleToggle?.(true);
      }
    },
    [handleToggle],
  );

  const controlsId = `${itemKey}-controls`;
  const panelId = `${itemKey}-panel`;

  return useMemo(
    () => ({
      id: controlsId,
      'aria-controls': panelId,
      'aria-expanded': localOpenState,
      onClick,
      onKeyUp,
      isOpen: localOpenState,
    }),
    [localOpenState],
  );
}

export const AccordionItem = memo(
  ({
    as: Component = 'div',
    chevron,
    children,
    className,
    icon,
    label,
    role,
    ...config
  }: {
    as?: ElementType;
    chevron?: ReactNode;
    children?: ReactNode;
    className?: string;
    icon?: ReactNode;
    itemKey: string;
    label?: ReactNode;
    role?: HTMLElement['role'];
  } & AccordionItemConfig) => {
    const { isOpen, ...props } = useAccordionItem(config);
    const { id: controlsId, 'aria-controls': panelId } = props;
    return (
      <Component
        className={classNames(styles.accordionItem, className, {
          [styles.open]: isOpen,
          [styles.icon]: !!icon,
        })}
        role={role}
      >
        <AccordionItemButton
          type="button"
          className={styles.accordionItemButton}
          chevron={chevron}
          icon={icon}
          {...props}
        >
          {label}
        </AccordionItemButton>
        <div className={styles.accordionItemPanel}>
          <Collapse
            open={isOpen}
            id={panelId}
            aria-labelledby={controlsId}
            className={styles.accordionItemPanelContent}
            role="region"
          >
            {children}
          </Collapse>
        </div>
      </Component>
    );
  },
);
